<script lang="ts">
type WorkableWithChatId = Workable & { chatId?: string }

export type OnClickScheduleBar = (work: WorkableWithChatId) => void
// NOTE: 振り返り済みなどサーバー側で拒否されるケースがあるため成否を返す
export type OnMoveScheduleBar = (values: { id: string; from: string; to: string }) => Promise<boolean>
export type OnNewSchedule = (values: { from: string; to: string }) => void

// 渡されている間だけ、クリックが選択・空きのドラッグが範囲選択になる
export type BulkSelection = {
  selectedIds: readonly string[]
  onToggleSelect: (id: string) => void
  onSelectRange: (values: { from: Temporal.PlainTime; to: Temporal.PlainTime }) => void
  // NOTE: 振り返り済みなどサーバー側で拒否されるケースがあるため成否を返す
  onShiftSelected: (minutes: number) => Promise<boolean>
}
</script>

<script lang="ts" setup>
import { Temporal } from '@js-temporal/polyfill'
import { useCurrentElement } from '@vueuse/core'
import { SnackbarProgrammatic } from 'buefy'
import type { SetOptional } from 'type-fest'
import type { DragTracking, OnResize } from '@/src/components/ScheduleBar.vue'
import { useDraggingTimeline } from '@/src/hooks/useDraggingTimeline'
import { clampShiftMinutes, isOverlapWork } from '@/src/lib/dateTimeUtils'
import { assignLanesToWorks } from '@/src/lib/scheduleLaneUtils'
import { pxToIntervalMinutes } from '@/src/lib/timelineUtils'
import { showError } from '@/src/lib/toast'
import type { Workable } from '@/src/types/models'

const props = withDefaults(
  defineProps<{
    compactWorks?: ReadonlyArray<SetOptional<Workable, 'note'>>
    basicWorks?: readonly WorkableWithChatId[]
    isEditable?: boolean
    isDayOff?: boolean
    intervalEndTime?: string
    onClickScheduleBar?: OnClickScheduleBar
    onNewSchedule?: OnNewSchedule
    onMoveScheduleBar?: OnMoveScheduleBar
    bulkSelection?: BulkSelection
  }>(),
  {
    compactWorks: () => [],
    basicWorks: () => [],
  },
)

const element = useCurrentElement<HTMLDivElement>()
const draggable = computed(() => !!props.isEditable)
const selectionMode = computed(() => !!props.bulkSelection)
const selectedIds = computed<readonly string[]>(() => props.bulkSelection?.selectedIds ?? [])
const { dragging, startTime, endTime } = useDraggingTimeline(element, {
  enabled: draggable,
  onEnd: (result) => {
    if (props.bulkSelection) {
      props.bulkSelection.onSelectRange({ from: result.startTime, to: result.endTime })
      return
    }

    props.onNewSchedule &&
      props.onNewSchedule({
        from: result.startTime.toString({ smallestUnit: 'minute' }),
        to: result.endTime.toString({ smallestUnit: 'minute' }),
      })
  },
})
const basicWorksWithIsOverlap = computed<ReadonlyArray<WorkableWithChatId & { lane: number; maxOverlapCount: number }>>(
  () => {
    const basicWorks = props.basicWorks.map((basicWork) => ({
      ...basicWork,
      isOverlap: isOverlapWork(basicWork, props.basicWorks),
    }))

    // レーン(行)と最大重複数を割り当て
    return assignLanesToWorks(basicWorks)
  },
)

const selectedBasicWorks = computed<readonly WorkableWithChatId[]>(() =>
  props.basicWorks.filter(({ id }) => selectedIds.value.includes(id)),
)

const draggingBar = ref<{ id: string; offsetX: number }>()
const previewShiftMinutes = ref(0)

// NOTE: 1件だけならまとめる意味がないので単体ドラッグとして扱う
const bulkDragging = computed(
  () => !!draggingBar.value && selectedBasicWorks.value.length > 1 && selectedIds.value.includes(draggingBar.value.id),
)

const handleDragEnd = (): void => {
  draggingBar.value = undefined
  previewShiftMinutes.value = 0
}

const dragTracking: DragTracking = {
  onStart: (values) => {
    draggingBar.value = values
  },
  onEnd: handleDragEnd,
}

// タイムライン左端(0:00)からカーソルまでの距離
// NOTE: offsetXはtarget基準で、プレビューでずらしたバーの上では噛み合わず揺れる。
// useMouseInElementもdragover中は更新されないため、clientXから直接求める
const cursorLeft = (event: DragEvent): number => event.clientX - element.value.getBoundingClientRect().left

// 掴んだバーの開始時刻が、ドロップ位置で何分ずれるか
const shiftMinutesAt = (event: DragEvent, grabbed: { id: string; offsetX: number }): number | undefined => {
  const left = cursorLeft(event)
  const basicWork = props.basicWorks.find(({ id }) => id === grabbed.id)
  if (!basicWork) return undefined

  // NOTE: 例: 午前2時30分に時間変更した場合は150になる
  const totalMinutes = pxToIntervalMinutes(left - grabbed.offsetX)
  const from = Temporal.PlainDateTime.from(basicWork.from)
  const newFrom = from.with({ hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 })
  return from.until(newFrom).total({ unit: 'minutes' })
}

const onDragover = (event: DragEvent): void => {
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  if (!draggingBar.value || !bulkDragging.value) return

  // NOTE: 掴んだ1本しかドラッグ画像が出ないため、選択分の移動先をここで見せる
  const minutes = shiftMinutesAt(event, draggingBar.value)
  previewShiftMinutes.value = minutes === undefined ? 0 : clampShiftMinutes(selectedBasicWorks.value, minutes)
}

// NOTE: ドロップ後の位置は一括移動の楽観的更新が動かすため、ここで見せるのはドラッグ中だけでよい
const previewShiftMinutesOf = (id: string): number =>
  draggingBar.value && selectedIds.value.includes(id) ? previewShiftMinutes.value : 0

const notifyTimeUpdated = (): void => {
  new SnackbarProgrammatic().open({
    message: '時間を更新しました。',
    duration: 1500,
    position: 'is-bottom',
  })
}

const updateScheduleBarTime = async (values: { id: string; from: string; to: string }): Promise<void> => {
  if (!props.onMoveScheduleBar) return

  const success = await props.onMoveScheduleBar(values)
  if (!success) return

  notifyTimeUpdated()
}

const shiftSelectedScheduleBars = async (diffMinute: number): Promise<void> => {
  if (!props.bulkSelection) return

  const minutes = clampShiftMinutes(selectedBasicWorks.value, diffMinute)
  if (minutes === 0) {
    // NOTE: 掴んだ位置に戻しただけなら黙って終わる。動かそうとして0になった場合だけ理由を出す
    if (diffMinute !== 0) showError('当日の時間内に収まらないため、まとめて時間を動かせません。')
    return
  }

  const success = await props.bulkSelection.onShiftSelected(minutes)
  if (success) notifyTimeUpdated()
}

const onDrop = async (event: DragEvent): Promise<void> => {
  if (!draggable.value) return

  const grabbed = draggingBar.value
  const isBulk = bulkDragging.value
  handleDragEnd()
  if (!grabbed) return

  const diffMinute = shiftMinutesAt(event, grabbed)
  if (diffMinute === undefined) return

  // NOTE: 未選択のバーは選択モード中でも単体で動く
  if (isBulk) {
    await shiftSelectedScheduleBars(diffMinute)
    return
  }

  const basicWork = props.basicWorks.find(({ id }) => id === grabbed.id)
  if (!basicWork) return

  const to = Temporal.PlainDateTime.from(basicWork.to)
  basicWork.from = Temporal.PlainDateTime.from(basicWork.from).add({ minutes: diffMinute }).toString()
  basicWork.to = to.add({ minutes: diffMinute }).toString()

  await updateScheduleBarTime(basicWork)
}

// NOTE: リサイズ結果は時刻のみで返るため、元の日付を保ったまま時刻だけ差し替える
const withPlainTime = (dateTime: string, plainTime: string): string =>
  Temporal.PlainDateTime.from(dateTime).withPlainTime(Temporal.PlainTime.from(plainTime)).toString()

const handleResizeScheduleBar = (basicWork: WorkableWithChatId, values: Parameters<OnResize>[0]): Promise<void> =>
  updateScheduleBarTime({
    id: basicWork.id,
    from: withPlainTime(basicWork.from, values.from),
    to: withPlainTime(basicWork.to, values.to),
  })

const handleClickScheduleBar = (basicWork: WorkableWithChatId): void => {
  if (props.bulkSelection) {
    props.bulkSelection.onToggleSelect(basicWork.id)
    return
  }

  if (!props.isEditable && !basicWork.chat) return

  props.onClickScheduleBar && props.onClickScheduleBar(basicWork)
}
</script>

<template>
  <div
    class="user-timeline"
    data-test-selector="schedule-timeline-user"
    @drop.prevent="onDrop"
    @dragover.prevent="onDragover"
  >
    <DayOffBar v-if="isDayOff" />
    <BreakBar v-else-if="intervalEndTime" :end="intervalEndTime" title="勤務間インターバル" />

    <div class="user-timeline-compact">
      <ScheduleBarCompact
        v-for="plannedWork in compactWorks"
        :key="plannedWork.id"
        v-bind="plannedWork"
        data-test-selector="planned-work-schedule-bar"
      />
    </div>
    <div class="user-timeline-basic">
      <ScheduleBar
        v-for="basicWork in basicWorksWithIsOverlap"
        :key="basicWork.id"
        v-bind="basicWork"
        :lane="basicWork.lane"
        :total-lanes="basicWork.maxOverlapCount"
        :draggable="draggable"
        :selected="selectedIds.includes(basicWork.id)"
        :preview-shift-minutes="previewShiftMinutesOf(basicWork.id)"
        :drag-tracking="dragTracking"
        :on-click="() => handleClickScheduleBar(basicWork)"
        :on-resize="selectionMode ? undefined : (values) => handleResizeScheduleBar(basicWork, values)"
      />
      <!-- FIXME: fromとtoには必ず日本時間となる文字列を渡す必要がある -->
      <ScheduleBar
        v-show="dragging"
        id="new-schedule"
        :title="selectionMode ? '選択範囲' : '(タイトルなし)'"
        :is-selection-range="selectionMode"
        company-large-category-name=""
        :from="startTime.toString()"
        :to="endTime.toString()"
        note=""
        :color-hue="0"
        :lane="0"
        :total-lanes="1"
        is-new
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/stylesheets/application/resources';

.user-timeline {
  border-bottom: 1px solid resources.$border;
  height: resources.$app-timeline-height;
  position: relative;

  .user-timeline-compact {
    padding-top: 10px;
  }
}
</style>
